import { useEffect, useState } from "react";
import { adminCsrfHeaders } from "../lib/adminApi";
import { Link } from "wouter";
import { ArrowLeft, Download, RefreshCw, Mail, CheckCircle2, Clock, XCircle, Copy, Trash2, Sparkles } from "lucide-react";

interface DemoLeadRow {
  id: number;
  email: string;
  name: string | null;
  company: string | null;
  status: "pending" | "verified" | "expired";
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  verifiedAt: string | null;
  updatedAt: string;
  codesIssued: number;
  totalAttempts: number;
}

const STATUS_FILTERS = ["all", "verified", "pending", "expired"] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

function StatusPill({ status }: { status: DemoLeadRow["status"] }) {
  const cfg = {
    verified: { Icon: CheckCircle2, cls: "bg-green-100 text-green-800" },
    pending: { Icon: Clock, cls: "bg-amber-100 text-amber-800" },
    expired: { Icon: XCircle, cls: "bg-gray-200 text-gray-700" },
  }[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      <cfg.Icon className="h-3 w-3" />
      {status}
    </span>
  );
}

/**
 * Owner-admin Demo Leads view (task #430). Reads `/admin/demo/leads` (which
 * is rate-limited and returned newest-first, capped at the server limit) and
 * exposes a "Download CSV" button that hits `/admin/demo/leads.csv`. Filters
 * are server-driven (`?status=verified|pending|expired`) so changing the
 * dropdown re-issues the request rather than client-paging a stale set.
 */
export default function AdminDemoLeads() {
  const [rows, setRows] = useState<DemoLeadRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusFilter>("all");

  async function load(s: StatusFilter) {
    setLoading(true);
    setError(null);
    try {
      const url = s === "all" ? "/api/admin/demo/leads" : `/api/admin/demo/leads?status=${s}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        setError(`Failed to load leads (${res.status}).`);
        setRows([]);
        return;
      }
      setRows((await res.json()) as DemoLeadRow[]);
    } catch {
      setError("Network error loading demo leads.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(status); }, [status]);

  const counts = (rows ?? []).reduce(
    (acc, r) => { acc[r.status]++; return acc; },
    { verified: 0, pending: 0, expired: 0 } as Record<DemoLeadRow["status"], number>,
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <Link href="/admin" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-1">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
            </Link>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Mail className="h-5 w-5 text-indigo-600" /> Demo Leads
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load(status)}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-60"
              data-testid="demo-leads-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <a
              href="/api/admin/demo/leads.csv"
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 text-sm font-medium"
              data-testid="demo-leads-csv"
            >
              <Download className="h-3.5 w-3.5" /> Download CSV
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white border rounded-lg p-4">
            <div className="text-xs font-medium text-gray-500">Verified</div>
            <div className="text-2xl font-bold text-green-700">{counts.verified}</div>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <div className="text-xs font-medium text-gray-500">Pending</div>
            <div className="text-2xl font-bold text-amber-700">{counts.pending}</div>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <div className="text-xs font-medium text-gray-500">Expired</div>
            <div className="text-2xl font-bold text-gray-600">{counts.expired}</div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Filter:</span>
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`demo-leads-filter-${s}`}
              className={
                "px-3 py-1 rounded-full text-xs font-medium border " +
                (status === s ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-700 hover:bg-gray-50")
              }
            >
              {s}
            </button>
          ))}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
        )}

        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">Email</th>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Company</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Codes</th>
                <th className="text-right px-4 py-2">Attempts</th>
                <th className="text-left px-4 py-2">Created</th>
                <th className="text-left px-4 py-2">Verified</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(rows ?? []).map((r) => (
                <tr key={r.id} data-testid={`demo-lead-row-${r.id}`}>
                  <td className="px-4 py-2 font-medium text-gray-900">{r.email}</td>
                  <td className="px-4 py-2 text-gray-700">{r.name ?? "—"}</td>
                  <td className="px-4 py-2 text-gray-700">{r.company ?? "—"}</td>
                  <td className="px-4 py-2"><StatusPill status={r.status} /></td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.codesIssued}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.totalAttempts}</td>
                  <td className="px-4 py-2 text-xs text-gray-500">{new Date(r.createdAt).toLocaleString()}</td>
                  <td className="px-4 py-2 text-xs text-gray-500">{r.verifiedAt ? new Date(r.verifiedAt).toLocaleString() : "—"}</td>
                </tr>
              ))}
              {!loading && (rows?.length ?? 0) === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">No demo leads yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <DemoInvitationsPanel />
      </main>
    </div>
  );
}

interface DemoInvitationRow {
  id: number;
  token: string;
  label: string;
  tier: "standard" | "enterprise";
  persona: string | null;
  expiresAt: string | null;
  maxUses: number | null;
  useCount: number;
  usedAt: string | null;
  usedByEmail: string | null;
  usedByName: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  shareUrl: string;
}

/**
 * Owner-admin panel for issuing tailored demo links (task #448). The
 * Enterprise-Preview tier lifts requireNotDemo gates, list caps and the PDF
 * watermark for the redeeming visitor's session, so prospects can see the
 * full surface during a single 15-minute window. Persona pre-selection lets
 * us land them in the matching workspace (Finance/HR/Legal) on first paint.
 */
function DemoInvitationsPanel() {
  const [rows, setRows] = useState<DemoInvitationRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [tier, setTier] = useState<"standard" | "enterprise">("enterprise");
  const [persona, setPersona] = useState("");
  // Fixed expiry options per task #448 spec: 1 / 7 / 30 days, or never.
  // Using a string to safely encode "never" alongside the numeric choices.
  const [expiresInDays, setExpiresInDays] = useState<"1" | "7" | "30" | "never">("7");
  const [maxUses, setMaxUses] = useState("");
  const [copiedId, setCopiedId] = useState<number | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/demo/invitations", { credentials: "include" });
      if (!res.ok) {
        setError(`Failed to load invitations (${res.status}).`);
        setRows([]);
        return;
      }
      setRows((await res.json()) as DemoInvitationRow[]);
    } catch {
      setError("Network error loading invitations.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!label.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { label: label.trim(), tier };
      if (persona) body.persona = persona;
      body.expiresInDays = expiresInDays === "never" ? null : parseInt(expiresInDays, 10);
      const max = parseInt(maxUses, 10);
      if (Number.isFinite(max) && max > 0) body.maxUses = max;
      const res = await fetch("/api/admin/demo/invitations", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json", ...adminCsrfHeaders() },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        setError(data.error ?? `Create failed (${res.status})`);
        return;
      }
      setLabel("");
      setPersona("");
      setMaxUses("");
      await load();
    } finally {
      setCreating(false);
    }
  }

  async function revoke(id: number) {
    if (!confirm("Revoke this invitation? Existing sessions keep their tier; new redemptions will be blocked.")) return;
    const res = await fetch(`/api/admin/demo/invitations/${id}`, {
      method: "DELETE",
      credentials: "include",
      headers: adminCsrfHeaders(),
    });
    if (res.ok) await load();
    else setError(`Revoke failed (${res.status}).`);
  }

  async function copyLink(row: DemoInvitationRow) {
    try {
      await navigator.clipboard.writeText(row.shareUrl);
      setCopiedId(row.id);
      window.setTimeout(() => setCopiedId((cur) => (cur === row.id ? null : cur)), 1500);
    } catch { /* ignore clipboard failures */ }
  }

  return (
    <section className="bg-white border rounded-lg p-4 space-y-4" data-testid="demo-invitations-panel">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-violet-600" /> Demo Invitations
        </h2>
        <button
          onClick={() => void load()}
          className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
          data-testid="demo-invitations-refresh"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end" data-testid="demo-invitation-form">
        <div className="md:col-span-2">
          <label className="text-xs text-gray-500">Label</label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Acme University — VP Athletics"
            className="w-full rounded-md border px-2 py-1.5 text-sm"
            required
            data-testid="demo-invitation-label"
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">Tier</label>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value as "standard" | "enterprise")}
            className="w-full rounded-md border px-2 py-1.5 text-sm"
            data-testid="demo-invitation-tier"
          >
            <option value="standard">Standard</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500">Persona</label>
          <select
            value={persona}
            onChange={(e) => setPersona(e.target.value)}
            className="w-full rounded-md border px-2 py-1.5 text-sm"
            data-testid="demo-invitation-persona"
          >
            <option value="">— none —</option>
            <option>Finance</option>
            <option>HR</option>
            <option>Legal</option>
            <option>AD</option>
            <option>SA</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500">Expires</label>
          <select
            value={expiresInDays}
            onChange={(e) => setExpiresInDays(e.target.value as "1" | "7" | "30" | "never")}
            className="w-full rounded-md border px-2 py-1.5 text-sm"
            data-testid="demo-invitation-expires"
          >
            <option value="1">1 day</option>
            <option value="7">7 days</option>
            <option value="30">30 days</option>
            <option value="never">Never</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500">Max uses</label>
          <input
            type="number"
            min={1}
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
            placeholder="∞"
            className="w-full rounded-md border px-2 py-1.5 text-sm"
          />
        </div>
        <div className="md:col-span-6">
          <button
            type="submit"
            disabled={creating || !label.trim()}
            className="rounded-md bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium px-3 py-1.5 disabled:opacity-60"
            data-testid="demo-invitation-create"
          >
            {creating ? "Creating…" : "Create invitation"}
          </button>
        </div>
      </form>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-2 text-xs">{error}</div>
      )}

      <div className="overflow-hidden border rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-3 py-2">Label</th>
              <th className="text-left px-3 py-2">Tier</th>
              <th className="text-left px-3 py-2">Persona</th>
              <th className="text-right px-3 py-2">Used</th>
              <th className="text-left px-3 py-2">Used by</th>
              <th className="text-left px-3 py-2">Expires</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {(rows ?? []).map((r) => {
              const isExpired = r.expiresAt && new Date(r.expiresAt).getTime() < Date.now();
              const isExhausted = r.maxUses != null && r.useCount >= r.maxUses;
              const isRevoked = !!r.revokedAt;
              const status = isRevoked ? "revoked" : isExpired ? "expired" : isExhausted ? "exhausted" : "active";
              return (
                <tr key={r.id} data-testid={`demo-invitation-row-${r.id}`}>
                  <td className="px-3 py-2 font-medium text-gray-900">{r.label}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      r.tier === "enterprise" ? "bg-violet-100 text-violet-800" : "bg-indigo-100 text-indigo-800"
                    }`}>{r.tier}</span>
                  </td>
                  <td className="px-3 py-2 text-gray-700">{r.persona ?? "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {r.useCount}{r.maxUses != null ? ` / ${r.maxUses}` : ""}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700" data-testid={`demo-invitation-usedby-${r.id}`}>
                    {r.usedByEmail ? (
                      <div>
                        <div className="font-medium text-gray-900">{r.usedByEmail}</div>
                        {r.usedByName && <div className="text-gray-500">{r.usedByName}</div>}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {r.expiresAt ? new Date(r.expiresAt).toLocaleDateString() : "never"}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      status === "active" ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-700"
                    }`}>{status}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => void copyLink(r)}
                        className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-gray-50"
                        data-testid={`demo-invitation-copy-${r.id}`}
                      >
                        <Copy className="h-3 w-3" /> {copiedId === r.id ? "Copied!" : "Copy link"}
                      </button>
                      {!isRevoked && (
                        <button
                          type="button"
                          onClick={() => void revoke(r.id)}
                          className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                          data-testid={`demo-invitation-revoke-${r.id}`}
                        >
                          <Trash2 className="h-3 w-3" /> Revoke
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!loading && (rows?.length ?? 0) === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-xs text-gray-500">No invitations yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
